import yaml from "js-yaml";

import debugLogger from "../utils/debugLogger";

/**
 * Types for domain registry YAML files.
 * Each domain has a registry at public/config/domains/<domain>.registry.yml
 */

export interface RegistryEndpoint {
  name: string;
  method: string;
  path: string;
  description: string;
  auth: boolean;
  request?: {
    params?: Record<string, { type: string; required: boolean }>;
    query?: Record<string, { type: string; required: boolean }>;
    body?: Record<
      string,
      { type: string; required: boolean; enum?: string[]; alias?: string }
    >;
    headers?: Record<string, string>;
  };
  response?: {
    success?: { status: number; body: unknown };
    errors?: Array<{ status: number; body: unknown }>;
  };
  mockApi?: {
    middleware?: string;
    collection?: string;
    handler: string;
  };
}

export interface RegistryComponent {
  name: string;
  path: string;
  maturity: string;
  description: string;
  featureFlags?: string[];
}

export interface RegistryEvent {
  name: string;
  description: string;
  payload: unknown;
}

export interface RegistryCacheStrategy {
  ttl: number;
  strategy: string;
  invalidateOn: string[];
  storage: string;
}

export interface DomainRegistry {
  domain: string;
  version: string;
  description: string;
  featureFlag?: string;

  ui: {
    paths: Record<string, string>;
  };

  components: RegistryComponent[];

  endpoints: RegistryEndpoint[];

  events: {
    emits: RegistryEvent[];
    listens: RegistryEvent[];
  };

  cache: {
    strategies: Record<string, RegistryCacheStrategy>;
  };

  dataContract: string;
  service: string;
  module: string;
}

const registryCache = new Map<string, DomainRegistry>();

/**
 * Load a domain registry YAML file.
 * Files live at /config/domains/<domain>.registry.yml (served by Vite from public/).
 */
export async function loadDomainRegistry(
  domain: string,
): Promise<DomainRegistry> {
  const cached = registryCache.get(domain);
  if (cached) return cached;

  const url = `/config/domains/${domain}.registry.yml`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to load domain registry for "${domain}": ${response.status} ${response.statusText}`,
    );
  }

  const text = await response.text();
  const registry = yaml.load(text) as DomainRegistry;

  registryCache.set(domain, registry);
  return registry;
}

/**
 * Load all domain registries.
 */
export async function loadAllDomainRegistries(): Promise<
  Map<string, DomainRegistry>
> {
  const domains = [
    "auth",
    "organization",
    "project",
    "application",
    "user",
    "resource",
  ];

  const results = await Promise.allSettled(
    domains.map((d) => loadDomainRegistry(d)),
  );

  const registries = new Map<string, DomainRegistry>();
  for (let i = 0; i < domains.length; i++) {
    const result = results[i];
    if (result.status === "fulfilled") {
      registries.set(domains[i], result.value);
    } else {
      debugLogger.warn(
        `Failed to load registry for domain "${domains[i]}":`,
        result.reason,
      );
    }
  }

  return registries;
}

/**
 * Get endpoints for a domain from its registry.
 */
export function getEndpoints(registry: DomainRegistry): RegistryEndpoint[] {
  return registry.endpoints;
}

/**
 * Get UI paths for a domain from its registry.
 */
export function getUIPaths(registry: DomainRegistry): Record<string, string> {
  return registry.ui.paths;
}

/**
 * Get cache strategies for a domain from its registry.
 */
export function getCacheStrategies(
  registry: DomainRegistry,
): Record<string, RegistryCacheStrategy> {
  return registry.cache.strategies;
}

/**
 * Get a specific endpoint path from a domain registry (synchronous).
 * Requires the registry to have been loaded first via loadDomainRegistry().
 * Throws if the registry or endpoint is not found — never silently falls back.
 */
export function getEndpointPath(domain: string, endpointName: string): string {
  const registry = registryCache.get(domain);
  if (!registry) {
    throw new Error(
      `Domain registry "${domain}" not loaded. Ensure loadDomainRegistry("${domain}") is called during app initialization.`,
    );
  }
  const endpoint = registry.endpoints.find((e) => e.name === endpointName);
  if (!endpoint) {
    throw new Error(
      `Endpoint "${endpointName}" not found in "${domain}" registry. Check public/config/domains/${domain}.registry.yml.`,
    );
  }
  return endpoint.path;
}

/**
 * Get a specific endpoint from a domain registry (synchronous).
 * Requires the registry to have been loaded first via loadDomainRegistry().
 */
export function getRegistryEndpoint(
  domain: string,
  endpointName: string,
): RegistryEndpoint | undefined {
  const registry = registryCache.get(domain);
  if (!registry) return undefined;
  return registry.endpoints.find((e) => e.name === endpointName);
}

/**
 * Get all event names (emitted) from a domain registry (synchronous).
 */
export function getEmittedEventNames(domain: string): string[] {
  const registry = registryCache.get(domain);
  if (!registry) return [];
  return registry.events.emits.map((e) => e.name);
}

/**
 * Get all event names (listened) from a domain registry (synchronous).
 */
export function getListenedEventNames(domain: string): string[] {
  const registry = registryCache.get(domain);
  if (!registry) return [];
  return registry.events.listens.map((e) => e.name);
}

/**
 * Resolve an endpoint path with param substitution.
 * Replaces :param placeholders with provided values.
 *
 * Usage: resolveEndpointPath("organization", "getOrganization", { id: alias })
 */
export function resolveEndpointPath(
  domain: string,
  endpointName: string,
  params?: Record<string, string>,
): string {
  let path = getEndpointPath(domain, endpointName);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, encodeURIComponent(value));
    }
  }
  return path;
}

/**
 * Get an event name from the domain registry (emits or listens).
 * Throws if the domain registry is not loaded or the event is not found.
 */
export function getEventName(domain: string, eventName: string): string {
  const registry = registryCache.get(domain);
  if (!registry) {
    throw new Error(
      `Domain registry "${domain}" not loaded. Ensure loadDomainRegistry("${domain}") is called during app initialization.`,
    );
  }
  const event =
    registry.events.emits.find((e) => e.name === eventName) ||
    registry.events.listens.find((e) => e.name === eventName);
  if (!event) {
    throw new Error(
      `Event "${eventName}" not found in "${domain}" registry. Check public/config/domains/${domain}.registry.yml.`,
    );
  }
  return event.name;
}

/** Clear the registry cache (for testing). */
export function clearRegistryCache(): void {
  registryCache.clear();
}
